import { chain, find, get, mapValues, omit, keys, isFunction, pick, isEmpty, capitalize, filter, includes, sumBy } from "lodash"
import moment from "moment"
import pluralize from "pluralize"
import { COLORS, ANALYZE_FACTORS_TAKE, TOP_COMPOSITION_FACTORS } from "helpers/analyzeConstants"
import { METRIC_TYPES } from "helpers/assets/constants"
import { Types as LoaderTypes } from "model/data"
import { AGGREGATION_FUNCTIONS } from "helpers/factorClassification"
import { percentageNoSymbolFormatter } from "helpers/formatters/numbers"
import { getCurrencyScaleAndFormatterForValues } from "helpers/formatters/currency-scale"
import { FACTORS_THRESHOLD } from "helpers/analyzeConstants"

const EMPTY_OBJECT = {}

export const ANALYZE_SECTION = {
  ASSETS: "assets",
  TRENDS: "trends",
}

export const computeDisplayCategories = (items, keys) => chain(items)
  .filter(item => keys[item.id])
  .map(item => [item.category, item.selectedView])
  .flatten()
  .compact()
  .uniq()
  .value()

export const computeCategoryItems = (items, keys) => filter(items, item => keys[item.id])

// Security profile attribution panel needs all factors
// Analyze and security profile graphs only want subset (based on number of colors)
export const computeCategoryItemsSubset = (items, keys) => chain(items)
  .thru(items => computeCategoryItems(items, keys))
  .take(ANALYZE_FACTORS_TAKE)
  .map((item, index) => ({
    ...item,
    color: item.color || COLORS[index],
  }))
  .value()

export const computeNonZerosKeys = (data, overrideKeys, removeSmallValues = true) => {
  // Do not apply logic to overrideKeys, their values are already set
  const getter = (item) => omit(item, [...keys(overrideKeys), "index", "date"])
  const accumulatorFn = (items) => (value, key) => {
    // If items[key] is true, we are already displaying the key, no need to check again
    if (items[key]) { return true }
    if (!value && !items[key]) { return false }
    if (isFunction(removeSmallValues)) {
      return removeSmallValues(value)
    }
    if (removeSmallValues) {
      return Math.abs(value) >= FACTORS_THRESHOLD
    }
    return true
  }

  return chain(data)
    .reduce((items, item) => mapValues(getter(item), (value, key) => accumulatorFn(items)(value, key)), {})
    // We re-add the "date" key, otherwise the graph crashes
    // TODO: #182286918 computeNonZerosKeys should be able to return only item keys
    .merge({ ...overrideKeys, date: true })
    .value()
}

export const computePerformanceData = (items, performance) => {
  if (!hasPerformanceData(performance)) {
    return []
  }

  return chain(performance)
    .map((datum, index) => {
      const { date } = datum

      const result = chain(items)
        .map(item => ([
          item.id,
          item.fn
            ? item.fn(datum)
            : get(datum, item.field),
        ]))
        .filter(([, value]) => !Number.isNaN(value))
        .fromPairs()
        .value()

      result.date = moment(date)
      result.index = index

      return result
    })
    .filter(datum => datum)
    .value()
}

function hasPerformanceData(performanceDates) {
  return chain(performanceDates)
    .filter(date => date.trading !== 0 || date.alpha !== 0 || !isEmpty(date.factors))
    .isEmpty()
    .eq(false)
    .value()
}

export const computeSelectedOption = (options, displayType, defaultOption = EMPTY_OBJECT) =>
  find(options, { id: displayType }) || defaultOption

export const computeSelectedGroup = (selectedGroup, groupedContributorsData, percentEquityField = "totalPercentEquity") => {
  if (!selectedGroup) {
    return null
  }

  const selectedGroupData = find(groupedContributorsData, item => item.id === selectedGroup.id)

  if (!selectedGroupData) {
    return null
  }

  return {
    ...pick(selectedGroupData, ["name"]),
    ...selectedGroup,
    totalPercentEquity: get(selectedGroupData, percentEquityField, 0),
  }
}

export function addCompositionTopXRow(compositionBy, label) {
  const topXValue = chain(compositionBy)
    .take(TOP_COMPOSITION_FACTORS)
    .sumBy(item => Math.abs(item.value))
    .value()
  compositionBy.unshift({ label: `Top ${TOP_COMPOSITION_FACTORS} ${pluralize(label)}`, value: topXValue })
}

export function addCompositionTotalRow(compositionBy, groupLabel) {
  const total = sumBy(compositionBy, c => c.id === "NET" ? 0 : Math.abs(c.value))
  compositionBy.unshift({ label: `Total ${groupLabel}`, value: total })
}

export function createAnalyzeGroupingAndFilterReducer(section) {
  return {
    [LoaderTypes.LOAD_SUCCESS]: (state, { resource }) => {
      // Remove position attributes and tag filter and grouping on portfolio change
      if (resource === "portfolio") {
        const groupingField = section === ANALYZE_SECTION.ASSETS ? "grouping" : "contributorsGrouping"
        let grouping = get(state, groupingField, null)
        let filter = get(state, "filter", null)

        if (grouping && (grouping.isPositionAttribute || grouping.isTag)) {
          grouping = null
        }

        if (filter) {
          filter = chain(filter)
            .omit(["tag", "positionAttributes"])
            .thru(newFilter => isEmpty(newFilter) ? null : newFilter)
            .value()
        }

        return {
          ...state,
          [groupingField]: grouping,
          ...(section === ANALYZE_SECTION.ASSETS && { filter }),
        }
      }
      return state
    },
  }
}

export function computeOptions(categories, first, current, displayCategories) {
  if (first && current) {
    return filter(categories, category => includes(displayCategories, category.id))
  } else {
    return []
  }
}

export function computeFileName(portfolioName, displayTypeName, viewType, screenName, startDate, endDate) {
  return `${portfolioName} ${displayTypeName} ${capitalize(viewType)} ${screenName} ${startDate} - ${endDate}`
}

export function computeDisplayType(displayType, categories) {
  if (find(categories, cat => cat.id === displayType)) {
    return displayType
  }

  return get(categories, "0.id")
}

export function computeDisplayTypeName(displayType, categories) {
  if (isEmpty(categories)) {
    return ""
  }

  const category = find(categories, cat => cat.id === displayType)
  if (category) {
    return category.label
  }

  return categories[0].label
}

function filterCategoryItemToDisplay(item, styleExposureAggregation, categoryId) {
  if (item.selectedView === categoryId) {
    return true
  }

  if (item.category === categoryId) {
    if (!item.selectedView) {
      return true
    }

    if (styleExposureAggregation === AGGREGATION_FUNCTIONS.NONE) {
      return true
    }
  }

  return false
}

export function getAllCategoryItems(items, displayType, styleExposureAggregation) {
  return chain(items)
    .filter(item => filterCategoryItemToDisplay(item, styleExposureAggregation, displayType))
    .orderBy(item => Math.abs(item.lastValue), ["desc"])
    .value()
}

export function getOrderedCategoryItems(items, displayType, styleExposureAggregation) {
  return chain(items)
    .filter(item => filterCategoryItemToDisplay(item, styleExposureAggregation, displayType))
    .orderBy([item => item.favorite, item => Math.abs(item.lastValue)], ["asc", "desc"])
    .value()
}

export function getScaledPerformanceFormatter(metricType, values) {
  if (metricType !== METRIC_TYPES.CURRENCY_PERFORMANCE) {
    return { formatter: percentageNoSymbolFormatter, currencyScale: null }
  }

  return getCurrencyScaleAndFormatterForValues(values)
}
